import { httpGetRate } from "api/admin/stats";
import AdminSidebar from "components/layout/AdminSidebar";
import Container from "components/layout/Container";
import Main from "components/layout/Main";
import VButton from "components/ui/VButton";
import useAdminAccess from "hooks/useAdminAccess";
import { FormEvent, ReactNode, useEffect, useRef, useState } from "react";
import { useQuery } from "react-query";
import { showSuccess } from "utilities/misc";
import { post } from "utilities/network";
import {
  countryRatesFromConfig,
  feeFields,
  fieldsFromConfig,
  validateCountryRates,
  validateFields,
  btcFields,
  CountryRatesDraft,
  FormFields,
} from "./ratesForm";

const inputClass =
  "w-full rounded-lg border border-gray-300 px-3 py-2.5 text-gray-900 focus:border-gray-700 focus:ring-gray-700 disabled:bg-gray-100 disabled:text-gray-500";
const actionClass =
  "px-6 py-3 bg-black text-white rounded-lg disabled:opacity-50";

// A refetch may update untouched sections, but must never erase another form's edits.
function useDraft<T>(incoming: T) {
  const [draft, setDraft] = useState(incoming);
  const dirty = useRef(false);
  const serialized = JSON.stringify(incoming);
  useEffect(() => {
    if (!dirty.current) setDraft(JSON.parse(serialized));
  }, [serialized]);
  return {
    draft,
    change: (next: T) => {
      dirty.current = true;
      setDraft(next);
    },
    saved: () => {
      dirty.current = false;
    },
  };
}

function useSave() {
  const lock = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const save = async (action: () => Promise<void>) => {
    if (lock.current) return;
    lock.current = true;
    setBusy(true);
    setError("");
    try {
      await action();
    } catch (cause: any) {
      setError(
        cause?.response?.data?.message ||
          cause?.message ||
          "Unable to save. Please try again.",
      );
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };
  return { busy, error, setError, save };
}

async function saveConfig(path: string, payload: unknown) {
  const response = await post(path, payload);
  if (response?.status !== "Success")
    throw new Error(response?.message || "The change was not saved.");
  return response;
}

function Panel({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <section className="p-4 bg-white border border-gray-200 rounded-xl sm:p-6">
      <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
      <p className="mt-2 text-sm text-gray-600">{description}</p>
      {children}
    </section>
  );
}

function Field({
  id,
  label,
  unit,
  value,
  onChange,
  disabled,
  min = 0,
  max,
}: {
  id: string;
  label: string;
  unit?: string;
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  min?: number;
  max?: number;
}) {
  return (
    <div className="min-w-0">
      <label
        htmlFor={id}
        className="block mb-2 text-sm font-medium text-gray-700"
      >
        {label}
      </label>
      <div className="flex items-center gap-2">
        <input
          id={id}
          type="number"
          step="any"
          min={min}
          max={max}
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
          className={inputClass}
        />
        {unit && <span className="text-sm text-gray-500 shrink-0">{unit}</span>}
      </div>
    </div>
  );
}

const markets = [
  { code: "NGN", name: "Nigeria" },
  { code: "GHS", name: "Ghana" },
  { code: "KES", name: "Kenya" },
] as const;

function CountryRates({
  config,
  editable,
  applyImmediately,
  reload,
}: {
  config: any;
  editable: boolean;
  applyImmediately: boolean;
  reload: () => Promise<unknown>;
}) {
  const { draft, change, saved } = useDraft<CountryRatesDraft>(
    countryRatesFromConfig(config),
  );
  const [reason, setReason] = useState("");
  const [submitted, setSubmitted] = useState<string | null>(null);
  const { busy, error, setError, save } = useSave();
  const edit = (next: CountryRatesDraft) => {
    change(next);
    setSubmitted(null);
    setError("");
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!editable || busy || submitted) return;
    const validation =
      validateCountryRates(draft) ||
      (!reason.trim() ? "Enter a reason for this rate change." : "");
    if (validation) return setError(validation);
    void save(async () => {
      const response = await saveConfig("admin/transactions/setRate", {
        buy: draft.NGN.buy,
        sell: draft.NGN.sell,
        fiatRates: { GHS: draft.GHS, KES: draft.KES },
        altcoinSellOffsetPercent: draft.offset,
        reason: reason.trim(),
      });
      // The response, not the browser's role snapshot, determines the outcome.
      if (response.data?.applied === true) {
        setSubmitted(
          "All country rates saved and applied. No additional approval is required.",
        );
        showSuccess("Country rates saved and applied");
        saved();
        await reload();
        return;
      }
      const id = response.data?.approvalRequest?.id;
      setSubmitted(
        `Rate change${id ? ` #${id}` : ""} submitted. All three countries are included; live rates change only after approval.`,
      );
      showSuccess("Rate change submitted for approval");
    });
  };
  return (
    <Panel
      title="Buy and sell rates"
      description={
        applyImmediately
          ? "Set local currency units per 1 USD for each country. Super Admin changes take effect immediately when saved."
          : "Set local currency units per 1 USD for each country. Submit all three countries together for approval."
      }
    >
      <form onSubmit={submit} noValidate className="mt-6">
        <div className="grid gap-4 xl:grid-cols-3">
          {markets.map(({ code, name }) => (
            <fieldset
              key={code}
              className="min-w-0 p-4 border border-gray-200 rounded-lg"
              disabled={!editable || busy}
            >
              <legend className="px-2 font-semibold">
                {name}{" "}
                <span className="ml-1 text-sm font-normal text-gray-500">
                  {code}
                </span>
              </legend>
              <div className="grid grid-cols-2 gap-3">
                {(["sell", "buy"] as const).map((side) => (
                  <Field
                    key={side}
                    id={`rate-${code}-${side}`}
                    label={`${code} ${side} rate`}
                    value={draft[code][side]}
                    disabled={!editable || busy}
                    onChange={(value) =>
                      edit({
                        ...draft,
                        [code]: { ...draft[code], [side]: value },
                      })
                    }
                  />
                ))}
              </div>
              <p className="mt-3 text-xs text-gray-500">
                {code} per USD ·{" "}
                {code === "NGN"
                  ? "Both rates must be above zero."
                  : "0 means that side is not configured."}
              </p>
            </fieldset>
          ))}
        </div>
        <div className="grid gap-6 pt-6 mt-6 border-t border-gray-200 md:grid-cols-2">
          <div>
            <Field
              id="altcoin-offset"
              label="Altcoin sell offset"
              unit="%"
              value={draft.offset}
              max={99.999999}
              disabled={!editable || busy}
              onChange={(offset) => edit({ ...draft, offset })}
            />
            <p className="mt-2 text-xs text-gray-500">
              Shared across countries. Reduces the sell rate for
              non-stablecoins; buy rates stay unchanged.
            </p>
          </div>
          <div>
            <label
              htmlFor="rate-reason"
              className="block mb-2 text-sm font-medium"
            >
              Change reason
            </label>
            <textarea
              id="rate-reason"
              placeholder="Why should this rate change?"
              className={`${inputClass} min-h-[90px]`}
              disabled={!editable || busy}
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
                setSubmitted(null);
              }}
            />
          </div>
        </div>
        <p className="mt-4 text-xs text-gray-500">
          Applying valid sell rates also converts pending legacy liens into the
          account’s currency. Those liens block withdrawals until conversion.
        </p>
        {error && (
          <p role="alert" className="mt-4 text-sm text-red-700">
            {error}
          </p>
        )}
        {submitted && (
          <p
            role="status"
            className="p-3 mt-4 text-sm text-green-800 rounded-lg bg-green-50"
          >
            {submitted}
          </p>
        )}
        <div className="flex flex-wrap items-center justify-between gap-3 pt-5 mt-6 border-t border-gray-200">
          <span className="text-sm text-gray-500">
            {editable
              ? applyImmediately
                ? "Your Super Admin changes apply immediately and are recorded in the audit log."
                : "A second admin must approve this change."
              : "You have view-only access to country rates."}
          </span>
          {editable && (
            <VButton
              type="submit"
              status={busy ? "pending" : "idle"}
              disabled={Boolean(submitted)}
              className={actionClass}
            >
              {applyImmediately
                ? "Save all country rates"
                : "Submit all country rates for approval"}
            </VButton>
          )}
        </div>
      </form>
    </Panel>
  );
}

function SettingsForm({
  title,
  description,
  fields,
  initial,
  editable,
  endpoint,
  payload,
  action,
  reload,
  reasonRequired = false,
}: {
  title: string;
  description: string;
  fields: FormFields;
  initial: Record<string, string>;
  editable: boolean;
  endpoint: string;
  payload: (draft: Record<string, string>, reason: string) => unknown;
  action: string;
  reload: () => Promise<unknown>;
  reasonRequired?: boolean;
}) {
  const { draft, change, saved } = useDraft(initial);
  const [reason, setReason] = useState("");
  const { busy, error, setError, save } = useSave();
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!editable || busy) return;
    const validation =
      validateFields(draft, fields) ||
      (reasonRequired && !reason.trim()
        ? "Enter a reason for these fee changes."
        : "");
    if (validation) return setError(validation);
    void save(async () => {
      const response = await saveConfig(
        endpoint,
        payload(draft, reason.trim()),
      );
      showSuccess(
        response.data?.message || response.message || "Settings saved",
      );
      // Only this successfully saved section can adopt refreshed server values.
      saved();
      setReason("");
      await reload();
    });
  };
  return (
    <Panel title={title} description={description}>
      <form onSubmit={submit} noValidate className="mt-5">
        <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
          {fields.map((field) => (
            <Field
              key={field.key}
              id={`setting-${field.key}`}
              label={field.label}
              unit={field.unit}
              value={draft[field.key] ?? ""}
              min={field.min}
              max={field.max}
              disabled={!editable || busy}
              onChange={(value) => {
                change({ ...draft, [field.key]: value });
                setError("");
              }}
            />
          ))}
        </div>
        {reasonRequired && (
          <div className="mt-5">
            <label
              htmlFor="fee-reason"
              className="block mb-2 text-sm font-medium"
            >
              Fee change reason
            </label>
            <textarea
              id="fee-reason"
              className={inputClass}
              value={reason}
              disabled={!editable || busy}
              onChange={(event) => setReason(event.target.value)}
            />
          </div>
        )}
        {error && (
          <p role="alert" className="mt-4 text-sm text-red-700">
            {error}
          </p>
        )}
        <div className="pt-5 mt-5 border-t border-gray-200">
          {editable ? (
            <VButton
              type="submit"
              status={busy ? "pending" : "idle"}
              className={actionClass}
            >
              {action}
            </VButton>
          ) : (
            <p className="text-sm text-gray-500">
              You have view-only access to these settings.
            </p>
          )}
        </div>
      </form>
    </Panel>
  );
}

const otcFields: FormFields = (["sell", "buy"] as const).flatMap((side) =>
  markets.map(({ code }) => ({
    key: `${code}.${side}`,
    label: `OTC ${code} ${side} rate`,
    unit: `${code} / USD`,
    min: 0,
    positive: code === "NGN",
  })),
);

const Rates: React.FC = () => {
  const { can, session } = useAdminAccess();
  const applyImmediately =
    session?.status === "active" && session?.role?.key === "super_admin";
  const [navigationOpen, setNavigationOpen] = useState(false);
  const query = useQuery("rate", httpGetRate, {
    retry: false,
    refetchOnWindowFocus: false,
  });
  const data = query.data;
  return (
    <Container className="flex-col md:flex-row">
      <button
        type="button"
        aria-expanded={navigationOpen}
        aria-controls="rates-admin-navigation"
        onClick={() => setNavigationOpen((open) => !open)}
        className="px-5 py-4 text-left text-white bg-black md:hidden"
      >
        {navigationOpen ? "Hide admin navigation" : "Show admin navigation"}
      </button>
      <div
        id="rates-admin-navigation"
        className={navigationOpen ? "md:contents" : "hidden md:contents"}
      >
        <AdminSidebar />
      </div>
      <Main className="flex-1 min-w-0 pb-10 text-left">
        <h1 className="text-2xl font-semibold">Rates and fees</h1>
        <p className="mt-2 text-sm text-gray-600">
          Manage country exchange rates, OTC rates and fee settings.
        </p>
        {query.isError && (
          <div
            role="alert"
            className="p-4 mt-6 text-red-800 rounded-lg bg-red-50"
          >
            Unable to load the latest rates.{" "}
            {data
              ? "Your unsaved edits are still available."
              : "Retry before making changes."}
            <button
              type="button"
              onClick={() => void query.refetch()}
              className="ml-3 underline"
            >
              Retry
            </button>
          </div>
        )}
        {!data ? (
          !query.isError && (
            <p role="status" className="mt-8">
              Loading rates…
            </p>
          )
        ) : (
          <div className="mt-8 space-y-8">
            <CountryRates
              config={data.rate}
              editable={can("transactions.rates.edit")}
              applyImmediately={applyImmediately}
              reload={query.refetch}
            />
            <SettingsForm
              title="OTC buy and sell rates"
              description="Local currency per 1 USD for Nigeria, Ghana and Kenya. OTC rates are separate from regular rates and apply immediately when saved. A GHS or KES rate of 0 leaves that side unavailable."
              fields={otcFields}
              initial={Object.fromEntries(
                markets.flatMap(({ code }) =>
                  (["sell", "buy"] as const).map((side) => [
                    `${code}.${side}`,
                    countryRatesFromConfig(data.otcRate)[code][side],
                  ]),
                ),
              )}
              editable={can("transactions.otcRate.edit")}
              endpoint="admin/transactions/setOTCRate"
              payload={(draft) => ({
                buy: draft["NGN.buy"],
                sell: draft["NGN.sell"],
                fiatRates: {
                  GHS: { buy: draft["GHS.buy"], sell: draft["GHS.sell"] },
                  KES: { buy: draft["KES.buy"], sell: draft["KES.sell"] },
                },
              })}
              action="Save OTC rates"
              reload={query.refetch}
            />
            <SettingsForm
              title="Bitcoin sweeping fees"
              description="Network fee limits and input thresholds. Changes take effect immediately when saved."
              fields={btcFields}
              initial={fieldsFromConfig(data.btcFee, btcFields)}
              editable={can("transactions.fees.edit")}
              endpoint="admin/transactions/setBtcFee"
              payload={(draft) => ({
                buy: draft["NGN.buy"],
                sell: draft["NGN.sell"],
                fiatRates: {
                  GHS: { buy: draft["GHS.buy"], sell: draft["GHS.sell"] },
                  KES: { buy: draft["KES.buy"], sell: draft["KES.sell"] },
                },
              })}
              action="Save Bitcoin sweeping fees"
              reload={query.refetch}
            />
            <SettingsForm
              title="Fee settings · not active"
              description="These percentages are stored for configuration only. The current backend does not apply them to transaction charges. Saving here will not change customer fees."
              fields={feeFields}
              initial={fieldsFromConfig(data.v2Fees, feeFields)}
              editable={can("transactions.fees.edit")}
              endpoint="admin/transactions/setV2Fees"
              payload={(draft, reason) => {
                const v2Fees: any = {};
                for (const [path, value] of Object.entries(draft)) {
                  const parts = path.split(".");
                  let cursor = v2Fees;
                  for (const key of parts.slice(0, -1))
                    cursor = cursor[key] ??= {};
                  cursor[parts[parts.length - 1]] = Number(value);
                }
                return { v2Fees, reason };
              }}
              action="Save fee settings"
              reload={query.refetch}
              reasonRequired
            />
          </div>
        )}
      </Main>
    </Container>
  );
};
export default Rates;
